import {Calendar} from "primereact/calendar";
import {Checkbox} from "primereact/checkbox";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import {SelectButton, type SelectButtonPassThroughOptions} from "primereact/selectbutton";
import {Toast} from "primereact/toast";
import {Tooltip} from "primereact/tooltip";
import React, {type RefObject, useEffect, useRef} from "react";
import {useTranslation} from "react-i18next";

import type {GeoRefAddress} from "@reva/shared/geo-reference";
import type {RevaLanguage} from "@reva/shared/i18n";
import {MimeType} from "@reva/shared/mime-type";
import {formatValue} from "@reva/shared/number-format";
import {type AvmSpecSegment, SEGMENT} from "@reva/shared/reva-spec";
import {format, parseISO} from "date-fns";

import AppreciationTabInputText from "../../component/appreciation/AppreciationTabInputText";
import I18nErrorMessage from "../../component/I18nErrorMessage";
import MissingPermissionIndicator from "../../component/MissingPermissionIndicator";
import {useParameterValue} from "../../component/paramInput/paramHooks";
import {useLanguage, useRevaTranslation} from "../../hooks/languageHooks";
import {selectAddressData} from "../../redux/addressSlice";
import {
    loadMarketData,
    type MarketDataConstraints,
    type MarketDataQueryInput,
    type MarketDataQueryResult,
    selectMarketDataConstraints,
    selectMarketDataError,
    selectMarketDataLoading,
    selectMarketDataQueryInput,
    selectMarketDataResult,
    setMarketDataInputField,
} from "../../redux/marketDataInputSlice";
import {selectSegment} from "../../redux/objectParametersSlice";
import {selectPersistenceInitialized} from "../../redux/persistenceSlice";
import {useRevaDispatch, useRevaSelector} from "../../redux/revaHooks";
import {formatDate} from "../../util/dateUtil";
import {PERMISSION_RULE_MARKET_MARKETDATA} from "../../util/permissions";
import {getObjectTypeBySegment, OBJECT_TYPES} from "../../util/timelineAggregation";
import {getTimelineSegments} from "../../util/timelineUtil";
import PersistenceFinalMessage from "../object/PersistenceFinalMessage";
import SnapshotMessage from "../object/SnapshotMessage";

type ChangeInput = <K extends keyof MarketDataQueryInput>(key: K, value: MarketDataQueryInput[K]) => void;
type ToastRef = RefObject<Toast | null>;
type PriceKey = "minPrice" | "maxPrice" | "medianPrice";

export interface FormattedMarketData {
    offerCount: string;
    minPrice: string;
    maxPrice: string;
    medianPrice: string;
    minPriceTotal: string;
    maxPriceTotal: string;
    medianPriceTotal: string;
    queryId?: number;
    raw: MarketDataQueryResult;
}

interface CopyDataRow {
    desc: string;
    min: string;
    max: string;
    median: string;
}

export const isValidDistance = (value: number | null | undefined): value is number =>
    value != null && value > 0 && value <= 20;

const isMfhOnly = (buildingType: number[] | null | undefined) =>
    Array.isArray(buildingType) && buildingType.includes(11) && !buildingType.includes(19);

export const hasRentOutput = (segment: string | null, buildingType: number[] | null | undefined): boolean =>
    segment !== SEGMENT.MFHWGH_K || isMfhOnly(buildingType);

export function getAreaUnit(segment: string | null, buildingType: number[] | null | undefined): string {
    if (segment === SEGMENT.BUERO_K) return "NF";
    return hasRentOutput(segment, buildingType) ? "WF" : "WNF";
}

export function formatMarketDataResult(
    queryResult: MarketDataQueryResult,
    toBuy: boolean,
    round: boolean | undefined,
    language: RevaLanguage,
): FormattedMarketData {
    const fmtPrice = (value?: number) => formatValue(value, language, round && toBuy);
    const fmtTotal = (value?: number) => formatValue(value, language, true);
    return {
        offerCount: fmtTotal(queryResult.offerCount),
        minPrice: fmtPrice(queryResult.minPrice),
        maxPrice: fmtPrice(queryResult.maxPrice),
        medianPrice: fmtPrice(queryResult.medianPrice),
        minPriceTotal: fmtTotal(queryResult.minPriceTotal),
        maxPriceTotal: fmtTotal(queryResult.maxPriceTotal),
        medianPriceTotal: fmtTotal(queryResult.medianPriceTotal),
        queryId: queryResult.queryId,
        raw: queryResult,
    };
}

export function getMarketDataSegment(
    segment: AvmSpecSegment,
    buildingProject: number | string | null | undefined,
): AvmSpecSegment {
    if (segment === SEGMENT.GRST_K) return getTimelineSegments(getObjectTypeBySegment(segment, buildingProject)).sale;
    return segment === SEGMENT.WHG_M ? SEGMENT.WHG_K : segment;
}

export const hasTotal = (marketData: Partial<FormattedMarketData>, addTotal: boolean | undefined): boolean =>
    (marketData.raw?.minPriceTotal ?? 0) > 0 && !!addTotal;

/** Zeilen der kopierten Tabelle; Kaufpreise immer ohne Cent, unabhängig von „Ergebnisse runden“. */
export function buildCopyDataRows(
    marketData: Partial<FormattedMarketData>,
    {
        toBuy,
        areaUnit,
        language,
        desc,
        totalLabel,
        addTotal,
    }: {
        toBuy: boolean;
        areaUnit: string;
        language: RevaLanguage;
        desc: string;
        totalLabel: string;
        addTotal: boolean | undefined;
    },
): CopyDataRow[] {
    const fmtPrice = (key: PriceKey) =>
        `${toBuy ? formatValue(marketData.raw?.[key], language, true) : marketData[key]} €/m² ${areaUnit}`;
    const rows = [{desc, min: fmtPrice("minPrice"), max: fmtPrice("maxPrice"), median: fmtPrice("medianPrice")}];
    if (hasTotal(marketData, addTotal)) {
        rows.push({
            desc: totalLabel,
            min: `${marketData.minPriceTotal} €`,
            max: `${marketData.maxPriceTotal} €`,
            median: `${marketData.medianPriceTotal} €`,
        });
    }
    return rows;
}

type Translate = (key: string) => string;

export function buildLocationText(
    inputParams: MarketDataQueryInput,
    address: GeoRefAddress | undefined,
    t: Translate,
): string {
    const {postcode, municipality, district, region} = address ?? {postcode: t("general.loading")};
    const {areaFilter} = inputParams;
    if (areaFilter === "distance") return `${t("market.marketdata.filterDistance")} ${inputParams.distance} km`;
    if (areaFilter === "postalCodes" || !areaFilter) return `${postcode} ${municipality ?? ""}`;
    const label = t(areaFilterTypes.find((o) => o.value === areaFilter)?.labelI18n ?? "");
    const name = (
        {
            municipalityCodes: municipality,
            districtCodes: district,
            spatialPlanningRegionCodes: region,
        } as Record<string, string | undefined>
    )[areaFilter];
    return `${label} ${name ?? ""}`;
}

export function buildQueryText(
    inputParams: MarketDataQueryInput,
    {toBuy, language, t, locationText}: {toBuy: boolean; language: RevaLanguage; t: Translate; locationText?: string},
): string {
    const segment = OBJECT_TYPES.find((o) => o.value === inputParams.segment);
    const newlyBuild = neubauTypes.find((o) => o.value === (inputParams.newlyBuild ?? "all"));
    const buildingTypeOpts = inputParams.segment ? buildingTypeOptions[inputParams.segment] : undefined;
    const selectedTypes =
        Array.isArray(inputParams.buildingType) && inputParams.buildingType.length > 0
            ? inputParams.buildingType
                  .map((v) => buildingTypeOpts?.find((o) => o.value === v)?.labelI18n)
                  .filter((k): k is string => !!k)
            : null;
    const segmentLabel = selectedTypes ? selectedTypes.map((k) => t(k)).join(", ") : t(segment?.labelI18n ?? "");
    let text = `${segmentLabel}, ${locationText ? `${locationText}, ` : ""}${t(newlyBuild?.labelI18n ?? "")}`;
    const to = t("market.marketdata.to");
    const from = t("market.marketdata.from");

    const spaceText = (
        fromKey: "spaceLivingFrom" | "spacePlotFrom",
        toKey: "spaceLivingTo" | "spacePlotTo",
        label: string,
    ) => {
        const fromValue = inputParams[fromKey] ? formatValue(inputParams[fromKey], language, true) : null;
        const toValue = inputParams[toKey] ? formatValue(inputParams[toKey], language, true) : null;
        if (fromValue && toValue) return `, ${fromValue} ${to} ${toValue} m² ${label}`;
        if (fromValue) return `, ${from} ${fromValue} m² ${label}`;
        if (toValue) return `, ${to} ${toValue} m² ${label}`;
        return "";
    };
    text += spaceText("spaceLivingFrom", "spaceLivingTo", t("market.marketdata.space"));
    text += spaceText("spacePlotFrom", "spacePlotTo", t("market.marketdata.spacePlot"));

    const yoc = t("market.marketdata.yearOfConstruction");
    if (inputParams.yearOfConstructionFrom && inputParams.yearOfConstructionTo)
        text += `, ${yoc} ${inputParams.yearOfConstructionFrom} ${to} ${inputParams.yearOfConstructionTo}`;
    else if (inputParams.yearOfConstructionFrom) text += `, ${yoc} ${from} ${inputParams.yearOfConstructionFrom}`;
    else if (inputParams.yearOfConstructionTo) text += `, ${yoc} ${to} ${inputParams.yearOfConstructionTo}`;

    const priceFromValue = toBuy ? inputParams.priceFrom : inputParams.rentFrom;
    const priceFrom = priceFromValue ? formatValue(priceFromValue, language, true) : null;
    const priceToValue = toBuy ? inputParams.priceTo : inputParams.rentTo;
    const priceTo = priceToValue ? formatValue(priceToValue, language, true) : null;
    const priceLabel = t(`market.marketdata.${toBuy ? "price" : "rent"}`);
    if (priceFrom && priceTo) text += `, ${priceFrom} ${to} ${priceTo} €/m² ${priceLabel}`;
    else if (priceFrom || priceTo)
        text += `${priceFrom ? `, ${from} ${priceFrom} €/m²` : `, ${to} ${priceTo} €/m²`} ${priceLabel}`;

    return text;
}

export function buildDateText(inputParams: MarketDataQueryInput, language: RevaLanguage, t: Translate): string | null {
    const dateFrom = inputParams.dateFrom
        ? `${t("market.marketdata.from")} ${formatDate(inputParams.dateFrom, language)}`
        : null;
    const dateTo = inputParams.dateTo
        ? `${t("market.marketdata.to")} ${formatDate(inputParams.dateTo, language)}`
        : null;
    return dateFrom && dateTo ? `${dateFrom} ${dateTo}` : dateFrom || dateTo;
}

/** Für Word: veraltete HTML-Attribute funktionieren beim Einfügen zuverlässiger als CSS. */
export function buildCopyHtml(headline: string, dataRows: CopyDataRow[], t: Translate): string {
    const thStyle = "background-color:#F2F2F2;border-bottom:1px solid black;";
    const pStyle =
        "font-family:Arial;font-size:10pt;mso-para-margin-left:5.4;margin-left:5.4pt;margin-top:2pt;margin-bottom:2pt;";
    const thPStyle = `${pStyle}font-weight:normal;text-align:left;`;
    const th = (text: string, width = "") =>
        `<th width="${width}" valign="bottom" style="${thStyle}"><p style="${thPStyle}">${text}</p></th>`;
    const td = (text: string) => `<td valign="top"><p style="${pStyle}">${text}</p></td>`;

    const headerRow = `<tr>${th(headline, "44%")}${th("von")}${th(t("market.marketdata.to"))}${th("Median")}</tr>`;
    const bodyRows = dataRows
        .map(({desc, min, max, median}) => `<tr>${td(desc)}${td(min)}${td(max)}${td(median)}</tr>`)
        .join("");

    // Tabellen-Einzug (Word: tblInd, #19418); mso-table-lspace/-rspace:0, damit Word margin-left auswertet.
    const tableStyle =
        "width:17cm;border-collapse:collapse;margin-left:0.19cm;mso-table-lspace:0cm;mso-table-rspace:0cm;";

    return `<table cellspacing="0" style="${tableStyle}">
                <thead>${headerRow}</thead>
                <tbody>${bodyRows}</tbody>
              </table>`;
}

interface MarketDataOutputProps {
    marketData: Partial<FormattedMarketData>;
    inputParams: MarketDataQueryInput;
    loading: boolean;
    toastRef: ToastRef;
    toBuy: boolean;
}

function MarketDataOutput({marketData, inputParams, loading, toastRef, toBuy}: MarketDataOutputProps) {
    const {t} = useTranslation();
    const language = useLanguage();
    const address = useRevaSelector(selectAddressData);
    const [today] = React.useState(() => format(new Date(), "yyyy-MM-dd"));
    const areaUnit = getAreaUnit(inputParams.segment, inputParams.buildingType);
    if (!toBuy && !hasRentOutput(inputParams.segment, inputParams.buildingType)) return null;

    const rawOfferCount = marketData.raw?.offerCount;
    const locationText = buildLocationText(inputParams, address?.address, t);
    const dateText = buildDateText(inputParams, language, t);

    const copyTable = async () => {
        const headline = `VALUE ${t("market.category.market_data")}`;
        const priceLabel = t(`market.marketdata.${toBuy ? "offerPrices" : "offerRents"}`);
        const queryText = buildQueryText(inputParams, {toBuy, language, t, locationText});
        const desc = `${marketData.offerCount} ${priceLabel}, ${queryText}, Daten ${dateText}`;

        const dataRows = buildCopyDataRows(marketData, {
            toBuy,
            areaUnit,
            language,
            desc,
            totalLabel: t(`market.marketdata.${toBuy ? "priceTotal" : "rentTotal"}`),
            addTotal: inputParams.addTotal,
        });

        const html = buildCopyHtml(headline, dataRows, t);
        const filler = `\t${t("market.marketdata.from")}\t${t("market.marketdata.to")}\tMedian`;
        const plainRows = dataRows.map((r) => `${r.desc}\t${r.min}\t${r.max}\t${r.median}`).join("\n");
        const plainOutput = `${headline}\n${filler}\n${plainRows}`;

        try {
            await navigator.clipboard.write([
                new ClipboardItem({
                    [MimeType.TEXT_HTML]: new Blob([html], {type: MimeType.TEXT_HTML}),
                    [MimeType.TEXT_PLAIN]: new Blob([plainOutput], {type: MimeType.TEXT_PLAIN}),
                }),
            ]);
            toastRef.current?.show({severity: "info", summary: "Info", detail: t("general.dataCopied")});
        } catch (e) {
            console.warn("Could not copy table content to clipboard", e);
        }
    };

    const offerText = `${marketData.offerCount} ${t(`market.marketdata.${toBuy ? "offersToBuy" : "offersToRent"}`)}`;
    const priceText = `${marketData.minPrice} - ${marketData.maxPrice} €/m² ${areaUnit}, Median ${marketData.medianPrice} €/m² ${areaUnit}`;
    const totalPriceText = `${marketData.minPriceTotal} - ${marketData.maxPriceTotal} €, Median ${marketData.medianPriceTotal} €`;
    const priceTitle = toBuy ? "offerPrices" : "offerRents";

    const rows = [
        {title: "object", text: buildQueryText(inputParams, {toBuy, language, t}), visible: true},
        {title: "location", text: locationText, visible: true},
        {
            title: "count",
            text: offerText + t("market.marketdata.added") + dateText,
            visible: rawOfferCount != null && rawOfferCount > 0,
        },
        {title: "count", text: t("market.marketdata.emptyResult"), visible: rawOfferCount === 0},
        {title: priceTitle, text: priceText, visible: rawOfferCount != null && rawOfferCount > 0},
        {
            title: toBuy ? "priceTotal" : "rentTotal",
            text: totalPriceText,
            visible: hasTotal(marketData, inputParams.addTotal),
        },
    ]
        .filter((r) => r.visible)
        .map((r) => ({text: r.text, title: t(`market.marketdata.${r.title}`)}));

    const header = (
        <div className="flex flex-row items-center">
            <h3 className="element-title m-0">
                {t(`market.marketdata.${toBuy ? "outputTitleBuy" : "outputTitleRent"}`, {
                    date: formatDate(today, language),
                })}
            </h3>
            <i
                className="pi pi-copy tooltip-icon marketdata-copy-icon text-primary cursor-pointer"
                data-pr-position="top"
                data-pr-tooltip={t("market.marketdata.copy")}
                onClick={() => void copyTable()}
            />
            {marketData.queryId != null && (
                <a
                    className="no-underline ml-auto text-primary cursor-pointer"
                    href={`https://analyst.value-marktdaten.de/abfrage.html?queryId=${marketData.queryId}`}
                    target="_blank"
                    rel="noreferrer"
                >
                    {t("market.marketdata.analyst")}
                </a>
            )}
        </div>
    );

    return (
        <div>
            <div className="element-panel">
                <DataTable value={rows} header={header} showHeaders={false} loading={loading}>
                    <Column field={"title"} bodyStyle={{width: "20%"}} />
                    <Column field={"text"} />
                </DataTable>
            </div>
        </div>
    );
}

interface BuildingTypeOption {
    value: number;
    label: string;
    labelI18n: string;
}

const buildingTypeOptions: Partial<Record<string, BuildingTypeOption[]>> = {
    EZFH_K: [
        {value: 7, label: "EFH", labelI18n: "object.differentiatedBuildingTypes.EZFH_K_7"},
        {value: 8, label: "RMH", labelI18n: "object.differentiatedBuildingTypes.EZFH_K_8"},
        {value: 9, label: "REH/DHH", labelI18n: "object.differentiatedBuildingTypes.EZFH_K_9"},
        {value: 10, label: "ZFH", labelI18n: "object.differentiatedBuildingTypes.EZFH_K_10"},
    ],
    MFHWGH_K: [
        {value: 11, label: "MFH", labelI18n: "object.differentiatedBuildingTypes.MFHWGH_K_11"},
        {value: 19, label: "WGH", labelI18n: "object.differentiatedBuildingTypes.MFHWGH_K_19"},
    ],
};

const neubauTypes: {value: MarketDataQueryInput["newlyBuild"]; labelI18n: string}[] = [
    {value: "new", labelI18n: "market.marketdata.newlybuild"},
    {value: "old", labelI18n: "market.marketdata.existing"},
    {value: "all", labelI18n: "market.marketdata.mixed"},
];

const areaFilterTypes = [
    {value: "distance", labelI18n: "market.marketdata.filterDistance"},
    {value: "postalCodes", labelI18n: "market.marketdata.filterRegion"},
    {value: "municipalityCodes", labelI18n: "market.marketdata.filterMunicipality"},
    {value: "districtCodes", labelI18n: "market.marketdata.filterDistrict"},
    {value: "spatialPlanningRegionCodes", labelI18n: "market.marketdata.filterRegionArea"},
];

const selectButtonPt: SelectButtonPassThroughOptions = {
    button: (options) => ({
        className: options?.context.selected ? "border text-primary bg-surface-0 dark:bg-surface-900" : "",
    }),
};

interface MarketDataInputProps {
    queryInput: MarketDataQueryInput;
    changeInput: ChangeInput;
    constraints: MarketDataConstraints;
    toastRef: ToastRef;
}

function MarketDataInput({queryInput, changeInput, constraints, toastRef}: MarketDataInputProps) {
    const {t} = useTranslation();
    const language = useLanguage();

    const stringToDate = (value: string | null | undefined) => (value ? parseISO(value) : null);
    const dateToString = (value: Date | null | undefined) => (value ? format(value, "yyyy-MM-dd") : null);
    const rangeParams = {values: queryInput, changeInput, constraints};
    const buildingTypes = queryInput.segment ? buildingTypeOptions[queryInput.segment] : undefined;

    return (
        <div>
            <div className="element-panel flex flex-col py-2">
                <div className="grid grid-cols-12 gap-0">
                    <div className="col-span-6 flex flex-col">
                        <label htmlFor="marketdata-segment" className="input-label font-bold !mt-2">
                            {t("market.marketdata.object_type")}
                        </label>
                        <Dropdown
                            inputId="marketdata-segment"
                            value={queryInput.segment}
                            className="md:w-[26rem]"
                            onChange={(e) => changeInput("segment", e.value)}
                            options={OBJECT_TYPES.map((o) => ({label: t(o.labelI18n), value: o.value}))}
                        />
                    </div>
                    {buildingTypes && (
                        <div className="col-span-6 pl-8 flex flex-col">
                            <label id="marketdata-building-type-label" className="input-label !mt-2 pl-1">
                                {t("market.marketdata.differentiated_object_type")}
                            </label>
                            <SelectButton
                                id="marketdata-building-type"
                                aria-labelledby="marketdata-building-type-label"
                                value={queryInput.buildingType}
                                pt={selectButtonPt}
                                className="pl-1"
                                multiple
                                onChange={(e) => changeInput("buildingType", e.value)}
                                options={buildingTypes.map((o) => ({label: o.label, value: o.value}))}
                            />
                        </div>
                    )}
                </div>
                <AreaFilterInput queryInput={queryInput} changeInput={changeInput} toastRef={toastRef} />
                <label id="marketdata-newly-build-label" className="input-label font-bold">
                    {t("market.marketdata.age")}
                </label>
                <SelectButton
                    id="marketdata-newly-build"
                    aria-labelledby="marketdata-newly-build-label"
                    value={queryInput.newlyBuild}
                    pt={selectButtonPt}
                    onChange={(e) => changeInput("newlyBuild", e.value)}
                    options={neubauTypes.map((o) => ({label: t(o.labelI18n), value: o.value}))}
                />
            </div>
            <div className="element-panel flex flex-col pb-2">
                <label htmlFor={"dateFrom"} className="input-label !mt-2">
                    {t("market.marketdata.date")}
                </label>
                <div className="flex items-center">
                    <Calendar
                        inputId={"dateFrom"}
                        value={stringToDate(queryInput.dateFrom)}
                        showIcon
                        className="flex-1"
                        yearNavigator={true}
                        maxDate={stringToDate(queryInput.dateTo ?? constraints.dateToMax) ?? undefined}
                        inputClassName="text-right"
                        onChange={(e) => changeInput("dateFrom", dateToString(e.value))}
                    />
                    <span className="mx-6">{t("market.marketdata.to")}</span>
                    <Calendar
                        value={stringToDate(queryInput.dateTo)}
                        showIcon
                        className="flex-1"
                        yearNavigator={true}
                        maxDate={stringToDate(constraints.dateToMax) ?? undefined}
                        viewDate={stringToDate(constraints.dateToMax)}
                        minDate={stringToDate(queryInput.dateFrom) ?? undefined}
                        placeholder={formatDate(constraints.dateToMax, language) ?? undefined}
                        inputClassName="text-right"
                        onChange={(e) => changeInput("dateTo", dateToString(e.value))}
                    />
                </div>
                <RangeInput label={t("market.marketdata.space")} parameter="spaceLiving" unit={"m²"} {...rangeParams} />
                <RangeInput label={t("market.marketdata.price")} parameter="price" unit={"€/m²"} {...rangeParams} />
                <RangeInput label={t("market.marketdata.rent")} parameter="rent" unit={"€/m²"} {...rangeParams} />
            </div>
            <div className="element-panel flex flex-col">
                {queryInput.segment !== SEGMENT.WHG_K && (
                    <RangeInput
                        label={t("market.marketdata.spacePlot")}
                        parameter="spacePlot"
                        unit={"m²"}
                        labelClassName="input-label !mt-2"
                        allowUnknown
                        {...rangeParams}
                    />
                )}
                <RangeInput
                    label={t("market.marketdata.yearOfConstruction")}
                    parameter="yearOfConstruction"
                    labelClassName={queryInput.segment === SEGMENT.WHG_K ? "input-label !mt-2" : "input-label"}
                    allowUnknown
                    {...rangeParams}
                />
            </div>
            <div className="element-panel flex flex-col">
                <RangeInput
                    label={t("market.marketdata.percentile")}
                    parameter="percentile"
                    labelClassName="input-label !mt-2"
                    {...rangeParams}
                    constraints={{percentileMin: 5, percentileMax: 95}}
                />
                <div className="grid grid-cols-12 gap-0 mt-6">
                    <BooleanInput
                        className={"col-span-6"}
                        label={t("market.marketdata.showTotal")}
                        value={queryInput.addTotal}
                        parameter={"addTotal"}
                        onChange={(value) => changeInput("addTotal", value)}
                    />
                    <BooleanInput
                        className={"col-span-5 ml-8"}
                        label={t("market.marketdata.round")}
                        value={queryInput.round}
                        parameter={"round"}
                        onChange={(value) => changeInput("round", value)}
                    />
                </div>
            </div>
        </div>
    );
}

interface AreaFilterInputProps {
    queryInput: MarketDataQueryInput;
    changeInput: ChangeInput;
    toastRef: ToastRef;
}

function AreaFilterInput({queryInput, changeInput, toastRef}: AreaFilterInputProps) {
    const {t} = useTranslation();
    // Remount setzt das Feld nach einer abgelehnten Eingabe auf den gespeicherten Wert zurück.
    const [distanceInputKey, setDistanceInputKey] = React.useState(0);
    const checkAndChangeDistance = (value: number | null | undefined) => {
        if (!isValidDistance(value)) {
            toastRef.current?.show({
                severity: "error",
                summary: t("error.error"),
                detail: t("market.marketdata.distanceError"),
            });
            setDistanceInputKey((k) => k + 1);
        } else {
            changeInput("distance", value);
        }
    };

    return (
        <div className="grid grid-cols-12 gap-0">
            <div className="col-span-6 flex flex-col">
                <label id="marketdata-area-filter-label" className="input-label font-bold">
                    {t("market.marketdata.regional_level")}
                </label>
                <SelectButton
                    id="marketdata-area-filter"
                    aria-labelledby="marketdata-area-filter-label"
                    value={queryInput.areaFilter}
                    pt={selectButtonPt}
                    onChange={(e) => changeInput("areaFilter", e.value)}
                    options={areaFilterTypes.map((o) => ({label: t(o.labelI18n), value: o.value}))}
                />
            </div>
            {queryInput.areaFilter === areaFilterTypes[0].value && (
                <div className="col-span-6 pl-8 flex flex-col">
                    <label htmlFor={"distance"} className="input-label font-bold pl-1">
                        {`${t("market.marketdata.distance")} *`}
                    </label>
                    <div className="p-inputgroup pl-1">
                        <InputNumber
                            key={distanceInputKey}
                            inputId={"distance"}
                            value={queryInput.distance}
                            maxFractionDigits={1}
                            min={0}
                            inputClassName="text-right"
                            onValueChange={(e) => checkAndChangeDistance(e.value)}
                        />
                        <UnitSuffix unit={"km"} />
                    </div>
                </div>
            )}
        </div>
    );
}

interface BooleanInputProps {
    label: string;
    parameter: string;
    value: boolean | undefined;
    onChange: (value: boolean) => void;
    className?: string;
}

function BooleanInput({label, parameter, value, onChange, className}: BooleanInputProps) {
    return (
        <div className={`${className} pl-1 mb-2 flex items-center whitespace-nowrap`}>
            <Checkbox inputId={parameter} checked={!!value} onChange={(e) => onChange(!!e.checked)} />
            <label htmlFor={parameter} className="mx-4">
                {label}
            </label>
        </div>
    );
}

type RangeConstraints = MarketDataConstraints & {percentileMin?: number; percentileMax?: number};
type RangeParameter = "spaceLiving" | "price" | "rent" | "spacePlot" | "yearOfConstruction" | "percentile";

interface RangeInputProps {
    label: string;
    values: MarketDataQueryInput;
    parameter: RangeParameter;
    unit?: string;
    labelClassName?: string;
    changeInput: ChangeInput;
    constraints: RangeConstraints;
    allowUnknown?: boolean;
}

function RangeInput({
    label,
    values,
    parameter,
    unit,
    labelClassName,
    changeInput,
    constraints,
    allowUnknown,
}: RangeInputProps) {
    const {t, language} = useRevaTranslation();
    const from = `${parameter}From` as const;
    const to = `${parameter}To` as const;
    const includeUnknown = `${parameter}IncludeUnknown` as
        | "spacePlotIncludeUnknown"
        | "yearOfConstructionIncludeUnknown";
    const constraint = (bound: "Min" | "Max") =>
        constraints[`${parameter}${bound}` as keyof RangeConstraints] as number | undefined;
    return (
        <>
            <label htmlFor={from} className={labelClassName ?? "input-label"}>
                {label}
            </label>
            <div className="flex items-center">
                <div className="p-inputgroup">
                    <InputNumber
                        inputId={from}
                        value={values[from]}
                        placeholder={formatValue(constraint("Min"), language, true, !unit)}
                        min={0}
                        max={values[to] ?? undefined}
                        maxFractionDigits={0}
                        inputClassName="text-right"
                        useGrouping={!!unit}
                        onValueChange={(e) => changeInput(from, e.value ?? null)}
                    />
                    <UnitSuffix unit={unit} />
                </div>
                <span className="mx-6">{t("market.marketdata.to")}</span>
                <div className="p-inputgroup">
                    <InputNumber
                        value={values[to]}
                        placeholder={formatValue(constraint("Max"), language, true, !unit)}
                        min={values[from] ?? 0}
                        maxFractionDigits={0}
                        inputClassName="text-right"
                        useGrouping={!!unit}
                        onValueChange={(e) => changeInput(to, e.value ?? null)}
                    />
                    <UnitSuffix unit={unit} />
                </div>
                {allowUnknown && (
                    <BooleanInput
                        className="ml-8 shrink-0"
                        label={t("market.marketdata.includeUnknown")}
                        value={values[includeUnknown]}
                        parameter={includeUnknown}
                        onChange={(value) => changeInput(includeUnknown, value)}
                    />
                )}
            </div>
        </>
    );
}

function UnitSuffix({unit}: {unit?: string}) {
    if (!unit) return null;
    return <span className="p-inputgroup-addon xl:shrink-0">{unit}</span>;
}

export function TabMarketData() {
    const dispatch = useRevaDispatch();
    const language = useLanguage();
    const initialised = useRevaSelector(selectPersistenceInitialized);
    const segment = useRevaSelector(selectSegment) as AvmSpecSegment;
    const {parameterValues: buildingProjectValue} = useParameterValue("building_project", "AVM");
    const marketdataError = useRevaSelector(selectMarketDataError);
    const toastRef = useRef<Toast>(null);
    const queryInput = useRevaSelector(selectMarketDataQueryInput);
    const result = useRevaSelector(selectMarketDataResult);
    const constraints = useRevaSelector(selectMarketDataConstraints);
    const loading = useRevaSelector(selectMarketDataLoading);

    useEffect(() => {
        if (initialised && segment != null && queryInput.segment == null) {
            const value = getMarketDataSegment(segment, buildingProjectValue?.activeParameterValue);
            dispatch(setMarketDataInputField({key: "segment", value}));
        }
        void dispatch(loadMarketData());
    }, [dispatch, initialised, segment, buildingProjectValue?.activeParameterValue, queryInput.segment]);

    const changeInput: ChangeInput = (key, value) => {
        dispatch(setMarketDataInputField({key, value}));
        void dispatch(loadMarketData());
    };

    const formatResult = (queryResult: MarketDataQueryResult, toBuy: boolean) =>
        formatMarketDataResult(queryResult, toBuy, queryInput.round, language);
    const buyQueryResult = result ? formatResult(result.buyQueryResult, true) : {};
    const rentQueryResult = result ? formatResult(result.rentQueryResult, false) : {};

    return (
        <div className="flex-1">
            <MissingPermissionIndicator permissionRule={PERMISSION_RULE_MARKET_MARKETDATA}>
                <I18nErrorMessage errorObject={marketdataError} className="flex mb-4 w-full" />
                <PersistenceFinalMessage />
                <SnapshotMessage />
                <AppreciationTabInputText page="market" tab="marketdata" />
                <div className="grid grid-cols-12 gap-0 w-full h-full">
                    <div className="col-span-6">
                        <MarketDataInput
                            changeInput={changeInput}
                            queryInput={queryInput}
                            constraints={constraints ?? {}}
                            toastRef={toastRef}
                        />
                    </div>
                    <div className="col-span-6 pl-4">
                        <MarketDataOutput
                            marketData={buyQueryResult}
                            inputParams={queryInput}
                            loading={loading}
                            toastRef={toastRef}
                            toBuy={true}
                        />
                        <MarketDataOutput
                            marketData={rentQueryResult}
                            inputParams={queryInput}
                            loading={loading}
                            toastRef={toastRef}
                            toBuy={false}
                        />
                        <Tooltip target=".marketdata-copy-icon" className="custom-tooltip" />
                        <Toast ref={toastRef} position="top-center" />
                    </div>
                </div>
            </MissingPermissionIndicator>
        </div>
    );
}
